import { ApiErrorPageWithTrans } from '@components/Error/ApiErrorPageWithTrans';
import { ContentWrapperCustomerApp } from '@components/Layouts/ContentWrapperCustomerApp';
import { NoddiLoadingScreenWithTrans } from '@components/Loading/NoddiLoadingScreenWithTrans';
import { CustomerLoginModal } from '@components/Login/CustomerLoginModal';
import { t } from '@lingui/core/macro';
import { LeadGenerationType, TireLeadReason, TireSeason } from '@navio/nsdk';
import { invalidateQuery, noddiAsync, URLKeys } from 'noddi-async';
import { BookingAddress } from 'noddi-types';
import { cn } from 'noddi-ui-common';
import { NoddiButton, NoddiStepFlow } from 'noddi-ui-web';
import { useAuthContext, useShouldFireRequestForLoggedInUser } from 'noddi-web';
import { useState } from 'react';
import { useCurrentBrand } from '../../../stores/BrandStore/brandStore';
import { CarStep } from './CarStep';
import { DetailsStep } from './DetailsStep';
import { MultiSelectStep } from './MultiSelectStep';
import {
  getActiveStepKeys,
  getLeadKey,
  getLeadTitle,
  getReasonOptions,
  getRequestOfferSteps,
  PassengerCarWheelPosition,
  POSITIONS_BY_TIRE_SELECTION,
  RequestOfferStepKey,
  TireSelection
} from './requestOfferOptions';
import { RequestSentScreen } from './RequestSentScreen';
import { RequestSummary } from './RequestSummary';
import { SeasonStep } from './SeasonStep';
import { TireSelectionField } from './TireSelectionField';
import { EMPTY_TIRE_SIZE_INPUT, getMissingAxles, getSizesToSend, TireSizeInput } from './tireSizes';
import { TireSizeStep } from './TireSizeStep';

export const RequestOffer = () => {
  const { isLoggedIn, getCurrentUserGroupId } = useAuthContext();
  const shouldFireRequest = useShouldFireRequestForLoggedInUser();
  const userGroupId = getCurrentUserGroupId();
  const { currentBrand } = useCurrentBrand();
  const [selectedCarIds, setSelectedCarIds] = useState<number[]>([]);
  const [selectedSeasons, setSelectedSeasons] = useState<TireSeason[]>([]);
  // Kept when winter is deselected, but only a winter lead sends it.
  const [isStudded, setIsStudded] = useState<boolean | null>(null);
  const [tireSelection, setTireSelection] = useState<TireSelection>('ALL');
  const [customPositions, setCustomPositions] = useState<PassengerCarWheelPosition[]>([]);
  const [selectedReasons, setSelectedReasons] = useState<TireLeadReason[]>([]);
  // Sizes the customer gave per car and season, for axles without a registered size.
  const [sizeInputs, setSizeInputs] = useState<Record<string, TireSizeInput>>({});
  const [selectedAddress, setSelectedAddress] = useState<BookingAddress | undefined>(undefined);
  const [description, setDescription] = useState('');
  const [stepKey, setStepKey] = useState<RequestOfferStepKey>('CAR');
  // Which way the last step change went, so the next step slides in from that side.
  const [direction, setDirection] = useState<'forward' | 'back'>('forward');
  const [isSubmitting, setIsSubmitting] = useState(false);
  // One lead per car and season. Keys of the ones the backend accepted, so a retry after a partial
  // failure sends only the rest instead of duplicating leads staff would have to clean up.
  const [sentLeadKeys, setSentLeadKeys] = useState<string[]>([]);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const { mutateAsync: createLeadGeneration, error: createLeadError } = noddiAsync.usePost({
    type: URLKeys.postCreateLead,
    queryConfig: {
      // A request saves typed tire sizes on the car, so a retry after a partial failure shows them as registered.
      onSuccess: () => invalidateQuery({ urlKey: URLKeys.getPaginatedUserGroupCarsForBooking })
    }
  });

  const {
    isLoading: isCarsLoading,
    data: cars,
    error: carsError
  } = noddiAsync.useGet({
    type: URLKeys.getPaginatedUserGroupCarsForBooking,
    input: { userGroupId: userGroupId! },
    queryConfig: {
      enabled: shouldFireRequest
    }
  });

  const {
    isLoading: isBookingsLoading,
    data: bookings,
    error: bookingsError
  } = noddiAsync.useGet({
    type: URLKeys.getPaginatedBookings,
    input: {
      userGroupId: userGroupId!,
      pageSize: 2,
      pageIndex: 0
    },
    queryConfig: {
      enabled: shouldFireRequest
    }
  });

  if (isCarsLoading || isBookingsLoading) {
    return <NoddiLoadingScreenWithTrans />;
  }

  if (carsError || bookingsError) {
    return <ApiErrorPageWithTrans error={[carsError, bookingsError]} />;
  }

  if (isSubmitted) {
    return <RequestSentScreen />;
  }

  const carList = cars?.results ?? [];
  const askForAddress = bookings?.results.length === 0;
  const positions = tireSelection === 'CUSTOM' ? customPositions : POSITIONS_BY_TIRE_SELECTION[tireSelection];
  const isWinterSelected = selectedSeasons.includes(TireSeason.WINTER);

  // The brand opens the request to every tire sales department of its marketplace; the address hands
  // it to the one covering the customer when exactly one does.
  const addressId = selectedAddress?.id ?? bookings?.results[0]?.address.id;

  // A selected car can leave the list (user group switch, ended ownership) while the page is open.
  const carsToRequest = carList.filter((car) => selectedCarIds.includes(car.id));
  const leadRequests = carsToRequest.flatMap((car) =>
    selectedSeasons.map((season) => {
      const key = getLeadKey(car.id, season);
      const missingAxles = getMissingAxles(car, season);
      const sizeInput = sizeInputs[key] ?? EMPTY_TIRE_SIZE_INPUT;
      return { car, season, key, missingAxles, sizeInput, sizesToSend: getSizesToSend(missingAxles, sizeInput) };
    })
  );
  const unsentLeadRequests = leadRequests.filter(({ key }) => !sentLeadKeys.includes(key));
  // Asked when a picked car has no size registered for the season; its model's sizes are offered to pick from.
  const sizeRequests = leadRequests.filter(({ missingAxles }) => missingAxles.length > 0);
  const stepKeys = getActiveStepKeys(sizeRequests.length > 0);

  const isStepValid: Record<RequestOfferStepKey, boolean> = {
    CAR: carsToRequest.length > 0,
    // Winter tires cannot be offered before the customer says whether they want studs.
    SEASON: selectedSeasons.length > 0 && (!isWinterSelected || isStudded !== null),
    TIRES: positions.length > 0,
    // Tires cannot be offered without their size: every picked tire needs one, registered or given.
    SIZE: leadRequests.every(({ sizesToSend }) => sizesToSend !== null),
    REASONS: selectedReasons.length > 0,
    DETAILS: addressId !== undefined
  };
  const stepIndex = stepKeys.indexOf(stepKey);
  const previousStepKey = stepKeys[stepIndex - 1];
  const nextStepKey = stepKeys[stepIndex + 1];

  const goToStep = (next: RequestOfferStepKey) => {
    setDirection(stepKeys.indexOf(next) > stepIndex ? 'forward' : 'back');
    setStepKey(next);
  };

  const submit = async () => {
    if (!stepKeys.every((key) => isStepValid[key])) {
      return;
    }

    const tirePositions = positions.map((position) => ({ position, reasons: selectedReasons }));

    setIsSubmitting(true);
    try {
      // The customer app raises no mutation toasts; the details step shows the failure. Settle every request so one
      // failure neither hides the others' outcome nor lands on the thank-you page.
      const results = await Promise.allSettled(
        unsentLeadRequests.map(({ car, season, sizesToSend }) =>
          createLeadGeneration({
            type: LeadGenerationType.NEW_TIRES,
            brandDomain: currentBrand,
            addressId,
            carId: car.id,
            userGroupId,
            tireDetails: {
              season,
              isStudded: season === TireSeason.WINTER ? isStudded : null,
              positions: tirePositions,
              ...sizesToSend
            },
            description
          })
        )
      );
      const newlySentKeys = unsentLeadRequests
        .filter((_, index) => results[index]?.status === 'fulfilled')
        .map(({ key }) => key);
      setSentLeadKeys([...sentLeadKeys, ...newlySentKeys]);
      if (newlySentKeys.length === unsentLeadRequests.length) {
        setIsSubmitted(true);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ContentWrapperCustomerApp
      hideBackButton
      title={t`Request an offer`}
      subtitle={t`Fill out the form, and we will get in touch`}
    >
      <div className='flex flex-col gap-6'>
        <NoddiStepFlow
          steps={getRequestOfferSteps().filter(({ key }) => stepKeys.includes(key))}
          currentStepKey={stepKey}
          // Continue is gated on each step, so every step before the current one is answered.
          completedStepKeys={stepKeys.slice(0, stepIndex)}
          onStepChange={(key) => goToStep(stepKeys.find((stepKey) => stepKey === key) ?? 'CAR')}
        />

        <div
          key={stepKey}
          className={cn(
            'flex flex-col gap-6 motion-safe:animate-in motion-safe:duration-300 motion-safe:ease-out motion-safe:fade-in',
            direction === 'forward' ? 'motion-safe:slide-in-from-right-8' : 'motion-safe:slide-in-from-left-8'
          )}
        >
          {stepKey === 'CAR' && userGroupId !== undefined && (
            <CarStep
              userGroupId={userGroupId}
              cars={carList}
              selectedCarIds={selectedCarIds}
              onChange={setSelectedCarIds}
            />
          )}

          {stepKey === 'SEASON' && (
            <SeasonStep
              selectedSeasons={selectedSeasons}
              onSeasonsChange={setSelectedSeasons}
              isStudded={isStudded}
              onStuddedChange={setIsStudded}
            />
          )}

          {stepKey === 'TIRES' && (
            <TireSelectionField
              tireSelection={tireSelection}
              onTireSelectionChange={setTireSelection}
              customPositions={customPositions}
              onCustomPositionsChange={setCustomPositions}
            />
          )}

          {stepKey === 'SIZE' && (
            <TireSizeStep
              groups={sizeRequests.map(({ car, season, key, missingAxles, sizeInput }) => ({
                key,
                title: getLeadTitle(car, season),
                missingAxles,
                modelSizes: car.tireSizeOptions,
                input: sizeInput
              }))}
              onInputChange={(key, input) => setSizeInputs({ ...sizeInputs, [key]: input })}
            />
          )}

          {stepKey === 'REASONS' && (
            <MultiSelectStep
              question={t`Why do you need new tires?`}
              options={getReasonOptions()}
              selected={selectedReasons}
              onChange={setSelectedReasons}
              compact
            />
          )}

          {stepKey === 'DETAILS' && (
            <>
              <RequestSummary
                cars={carsToRequest}
                requests={leadRequests}
                tireSelection={tireSelection}
                positions={positions}
                reasons={selectedReasons}
                isStudded={isWinterSelected ? isStudded : null}
              />
              <DetailsStep
                askForAddress={askForAddress}
                selectedAddress={selectedAddress}
                onSelectAddress={setSelectedAddress}
                description={description}
                onDescriptionChange={setDescription}
                submitError={createLeadError}
                unsentRequests={
                  sentLeadKeys.length > 0
                    ? unsentLeadRequests.map(({ car, season, key }) => ({ key, value: getLeadTitle(car, season) }))
                    : []
                }
              />
            </>
          )}
        </div>

        <div className='flex justify-between gap-3'>
          <NoddiButton
            variant='outline'
            startIcon='ChevronLeft'
            className='flex-1 md:flex-none'
            disabled={!previousStepKey}
            onClick={() => previousStepKey && goToStep(previousStepKey)}
          >{t`Back`}</NoddiButton>
          {nextStepKey ? (
            <NoddiButton
              endIcon='ChevronRight'
              className='flex-1 md:flex-none'
              disabled={!isStepValid[stepKey]}
              onClick={() => goToStep(nextStepKey)}
            >{t`Continue`}</NoddiButton>
          ) : (
            <NoddiButton
              loading={isSubmitting}
              className='flex-1 md:flex-none'
              disabled={!isStepValid[stepKey]}
              // oxlint-disable-next-line typescript/no-misused-promises -- submit settles every request itself, so the returned promise never rejects
              onClick={submit}
            >{t`Submit`}</NoddiButton>
          )}
        </div>
      </div>
      <CustomerLoginModal
        open={!isLoggedIn}
        onClose={() => {}}
        title={t`Sign in to request an offer`}
        description={t`Please sign in to request an offer`}
        onLoginSuccess={() => {}}
      />
    </ContentWrapperCustomerApp>
  );
};
